import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Switch,
  Collapse,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import DecimalField from '../shared/DecimalField';

/**
 * Shared component for optional inventory tracking fields during part creation.
 * Shows a checkbox to enable tracking, and when checked, shows initial quantity
 * and shelf location fields.
 *
 * Only renders when the organization has inventory_tracking_enabled = true.
 */
function InventoryTrackingFields({
  inventoryEnabled,
  enableTracking,
  setEnableTracking,
  initialQuantity,
  setInitialQuantity,
  shelfLocation,
  setShelfLocation,
}) {
  const { t } = useTranslation();

  if (!inventoryEnabled) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enableTracking}
            onChange={(e) => setEnableTracking(e.target.checked)}
            size="small"
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{t('inventory.startTracking')}</Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', ml: 0 }}
      />
      <Collapse in={enableTracking}>
        <Box sx={{ pl: 4, pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5, mb: 0.5 }}>
            {t('inventory.startTrackingHelp')}
          </Typography>
          <DecimalField
            label={t('inventory.initialQuantity')}
            value={initialQuantity}
            onChange={setInitialQuantity}
            size="small"
            fullWidth
            helperText={t('inventory.initialQuantityHelp')}
          />
          <TextField
            label={t('inventory.shelfLocation')}
            value={shelfLocation}
            onChange={(e) => setShelfLocation(e.target.value)}
            size="small"
            fullWidth
            helperText={t('inventory.shelfLocationHelp')}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

export default InventoryTrackingFields;
